import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { Link } from 'site-modules/shared/components/link/link';
import { toShortFormattedAddress } from 'site-modules/shared/utils/address-format';
import { isCarMax, isCarZingPartner } from 'site-modules/shared/utils/dealer-details-utils';
import {
  getDealerWebsiteUrl,
  getDealerWebsitesValue,
  getDealerWebsiteTrackingParams,
  updateDealerWebsiteTrackingParams,
  addAdditionalUTMParams,
} from 'client/site-modules/shared/utils/dealership-link-constructor';
import { generateDeepLink } from 'client/site-modules/shared/utils/car-buying-online/generate-deep-link';
import { DealerInfoEntities } from 'client/data/models/dealer-info';
import { InventoryEntities } from 'client/data/models/inventory';

export function DealerLink({
  vinData = {},
  dealerInfo = {},
  visitorZipCode = '',
  isDealerWebsiteLinkHidden = false,
  isTopEmbeddedLeadFormEnabled = false,
  dataTrackingParent = '',
  dealerShipLink = '',
}) {
  const { address, franchiseId, rooftopId, displayInfo = {}, name } = get(vinData, 'dealerInfo', {});

  const deepLinkUrl = get(vinData, 'deepLinkUrl', getDealerWebsitesValue(dealerInfo));
  const dealerWebSiteUrl = getDealerWebsiteUrl(deepLinkUrl);
  const dealerWebSiteTrackingParams = getDealerWebsiteTrackingParams(deepLinkUrl);

  const genericDeepLinkUrl = getDealerWebsitesValue(dealerInfo);
  const genericDeepLink = generateDeepLink(vinData) || genericDeepLinkUrl;
  const dealer = get(vinData, 'dealerInfo.displayInfo.parentDealershipName');

  const genericUrl = `${getDealerWebsiteUrl(genericDeepLink)}${updateDealerWebsiteTrackingParams({
    deepLinkUrl: genericDeepLinkUrl,
    dealer,
    zipCode: visitorZipCode,
    isDealerInfo: true,
  })}`;

  const dealershipName = get(displayInfo, 'parentDealershipName', '');
  const dealerLinkUrl = isCarMax(dealershipName)
    ? addAdditionalUTMParams({ deepLink: genericUrl, dealer: dealershipName })
    : `${dealerWebSiteUrl}${dealerWebSiteTrackingParams}`;
  const partnerCode = vinData?.dealerInfo?.partnerCode;

  const carZingDealerUrl = isCarZingPartner(partnerCode) && vinData?.listingUrl;

  const nameWithAddress = (
    <span className="fw-bold">
      {name}
      <span className="ms-0_25 fw-normal">({toShortFormattedAddress(address)})</span>
    </span>
  );

  if (isDealerWebsiteLinkHidden) {
    return nameWithAddress;
  }

  if (carZingDealerUrl || dealerWebSiteUrl) {
    return (
      <Link
        to={carZingDealerUrl || dealerLinkUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-tracking-id="dealer_site_clickout"
        data-tracking-value={carZingDealerUrl || dealerWebSiteUrl}
        data-dealer-franchise-id={franchiseId}
        data-dealer-rooftop-id={rooftopId}
        className="text-decoration-none fw-medium text-dark"
        {...(carZingDealerUrl
          ? {
              'data-tracking-parent': `${dataTrackingParent}-${partnerCode?.includes('TRIAL') ? 'trial' : 'paid'}_carzing`,
            }
          : {})}
      >
        {nameWithAddress}
        <i
          className={classnames('icon-open_in_new ms-0_25', isTopEmbeddedLeadFormEnabled ? 'small' : 'medium')}
          aria-hidden
        />
        <span className="visually-hidden">external link</span>
      </Link>
    );
  }

  return (
    <Link
      to={dealerShipLink}
      data-tracking-id="vin_view_dealer_details"
      data-dealer-franchise-id={franchiseId}
      data-dealer-rooftop-id={rooftopId}
      className={classnames('text-decoration-none fw-medium dealership-link', {
        'd-block': !isTopEmbeddedLeadFormEnabled,
      })}
    >
      {nameWithAddress}
    </Link>
  );
}

DealerLink.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  dealerInfo: DealerInfoEntities.DealerInfo,
  visitorZipCode: PropTypes.string,
  isDealerWebsiteLinkHidden: PropTypes.bool,
  isTopEmbeddedLeadFormEnabled: PropTypes.bool,
  dataTrackingParent: PropTypes.string,
  dealerShipLink: PropTypes.string,
};
